import React, { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { PageHeader, DataTable, PrimaryButton } from "@/components/DataViews";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Edit } from "lucide-react";

const ROLES = ["Administrator", "Indent Creator", "Site Incharge", "QS Engineer", "Purchase Executive", "Purchase Manager", "Accounts Executive", "Accounts Manager", "Store Keeper", "Project Manager", "Management", "Auditor"];

export default function UsersList() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", employee_id: "", email: "", mobile: "", password: "", designation: "", department: "", reporting_manager: "", roles: [], status: "Active" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", employee_id: "", email: "", mobile: "", password: "", designation: "", department: "", reporting_manager: "", roles: [], status: "Active" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u.id);
    setForm({ ...u, password: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editing}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const toggleRole = (r) => {
    setForm((f) => ({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] }));
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="User Management"
        subtitle="Administrators can create users, assign roles, and manage access."
        actions={<PrimaryButton onClick={openNew} data-testid="new-user-btn"><Plus size={14} className="mr-2" />New User</PrimaryButton>}
      />
      <DataTable
        testid="users-table"
        columns={[
          { key: "employee_id", label: "Emp ID", render: (r) => <span className="font-mono text-xs">{r.employee_id}</span> },
          { key: "name", label: "Name" },
          { key: "email", label: "Email", render: (r) => <span className="text-xs">{r.email}</span> },
          { key: "designation", label: "Designation" },
          { key: "department", label: "Dept" },
          { key: "roles", label: "Roles", render: (r) => (
            <div className="flex flex-wrap gap-1">
              {r.roles?.map((role) => <span key={role} className="text-[10px] px-2 py-0.5 rounded bg-kabir/10 border border-kabir/20 text-zinc-800 dark:text-zinc-200">{role}</span>)}
            </div>
          )},
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          { key: "action", label: "", render: (r) => <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(r); }} data-testid={`edit-user-${r.employee_id}`}><Edit size={14} /></Button> },
        ]}
        data={users}
        empty="No users."
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-semibold">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div><Label className="text-xs uppercase font-semibold">Employee ID *</Label><Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="user-empid" /></div>
            <div><Label className="text-xs uppercase font-semibold">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" disabled={!!editing} /></div>
            <div><Label className="text-xs uppercase font-semibold">Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">{editing ? "New Password (blank = unchanged)" : "Password *"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div><Label className="text-xs uppercase font-semibold">Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
            <div><Label className="text-xs uppercase font-semibold">Reporting Manager</Label><Input value={form.reporting_manager} onChange={(e) => setForm({ ...form, reporting_manager: e.target.value })} /></div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold">Roles</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={form.roles.includes(r)} onCheckedChange={() => toggleRole(r)} data-testid={`role-${r.replace(/\s/g, "-").toLowerCase()}`} />
                  <span>{r}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <PrimaryButton onClick={save} data-testid="save-user">{editing ? "Save Changes" : "Create User"}</PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
